import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import AuthService from "../../api/services/AuthService";
import { alertErrorMessage } from "../CustomAlertMessage";
import "./referralDetails.css";

const PAGE_LIMIT = 20;

const fmtAmt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return parseFloat(n.toFixed(8)).toLocaleString(undefined, { maximumFractionDigits: 8 });
};

const fmtCoinList = (rows, amountKey) => {
  if (!Array.isArray(rows) || rows.length === 0) return "—";
  return rows
    .map((row) => `${fmtAmt(row?.[amountKey] || 0)} ${row?.currency || ""}`.trim())
    .filter(Boolean)
    .join(", ");
};

const statusClass = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "ACTIVE" || s === "FILLED") return "text-success";
  if (s === "CANCELLED") return "text-danger";
  return "text-warning";
};

const commissionTypeLabel = (type) => {
  if (type === "instant_bonus") return "Instant bonus";
  if (type === "payout") return "Payout commission";
  return type || "—";
};

const rewardTypeLabel = (type) => {
  if (type === "signup_bonus") return "Signup bonus";
  if (type === "referral_bonus") return "Referral bonus";
  return type || "—";
};

const initialsFromName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0] || "?").slice(0, 2).toUpperCase();
};

const EmptyState = ({ text }) => (
  <div className="rd-empty">
    <div className="no_data_s">
      <img src="/images/no_data_vector.svg" className="img-fluid dark_img" width="96" height="96" alt="" />
      <img src="/images/no_data_vector_light.png" className="img-fluid light_img" alt="" />
    </div>
    <p>{text}</p>
  </div>
);

const ReferralDetailsSection = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [view, setView] = useState("list");
  const [list, setList] = useState([]);
  const [listSummary, setListSummary] = useState({ total_referrals: 0, by_level: {} });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, total_pages: 1 });
  const [level, setLevel] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detailPagination, setDetailPagination] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [innerTab, setInnerTab] = useState("staking");
  const [expandedPackage, setExpandedPackage] = useState(null);

  const handleAuthFailure = useCallback(
    (result) => {
      const msg = String(result?.message || "").toLowerCase();
      if (msg.includes("unauthorized") || result?.status === 401) {
        navigate("/login");
        return true;
      }
      return false;
    },
    [navigate]
  );

  const fetchList = useCallback(
    async (page = 1, opts = {}) => {
      if (!token) return;
      const nextLevel = opts.level !== undefined ? opts.level : level;
      const nextSearch = opts.search !== undefined ? opts.search : search;
      setListLoading(true);
      setListError("");
      try {
        const result = await AuthService.getReferralDetails({
          page,
          limit: PAGE_LIMIT,
          level: nextLevel || undefined,
          search: nextSearch || undefined,
        });
        if (handleAuthFailure(result)) return;
        if (result?.success) {
          setList(Array.isArray(result.data) ? result.data : []);
          setListSummary(result.summary || { total_referrals: 0, by_level: {} });
          setPagination({
            page: result.pagination?.page || page,
            limit: result.pagination?.limit || PAGE_LIMIT,
            total: result.pagination?.total ?? result.summary?.total_referrals ?? 0,
            total_pages: result.pagination?.total_pages || 1,
          });
        } else {
          setList([]);
          setListError(result?.message || "Failed to load referrals");
          alertErrorMessage(result?.message || "Failed to load referrals");
        }
      } catch {
        setList([]);
        setListError("Failed to load referrals");
        alertErrorMessage("Failed to load referrals");
      } finally {
        setListLoading(false);
      }
    },
    [token, level, search, handleAuthFailure]
  );

  const fetchDetail = useCallback(
    async (referralUserId, page = 1) => {
      if (!token || !referralUserId) return;
      setDetailLoading(true);
      setDetailError("");
      setForbidden(false);
      try {
        const result = await AuthService.getReferralDetailByUserId(referralUserId, {
          page,
          limit: PAGE_LIMIT,
        });
        if (handleAuthFailure(result)) return;
        const msg = String(result?.message || "");
        if (result && result.success === false && /referral network/i.test(msg)) {
          setForbidden(true);
          setDetail(null);
          return;
        }
        if (result?.success && result?.data) {
          setDetail(result.data);
          setDetailPagination(result.pagination || null);
          setDetailPage(result.pagination?.page || page);
          const pkgs = result.data?.staking?.packages || [];
          const bonuses = result.data?.referral_rewards?.items || [];
          const trades = result.data?.brokerage?.trades || [];
          if (page === 1) {
            if (pkgs.length > 0) setInnerTab("staking");
            else if (bonuses.length > 0) setInnerTab("bonus");
            else if (trades.length > 0) setInnerTab("brokerage");
            else setInnerTab("staking");
          }
        } else {
          setDetail(null);
          setDetailError(result?.message || "Failed to load referral details");
          alertErrorMessage(result?.message || "Failed to load referral details");
        }
      } catch {
        setDetail(null);
        setDetailError("Failed to load referral details");
        alertErrorMessage("Failed to load referral details");
      } finally {
        setDetailLoading(false);
      }
    },
    [token, handleAuthFailure]
  );

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    fetchList(1);
  }, [fetchList, level, search]);

  const openDetail = (userId) => {
    if (!userId) return;
    setSelectedId(userId);
    setView("detail");
    setInnerTab("staking");
    setExpandedPackage(null);
    fetchDetail(userId, 1);
  };

  const backToList = () => {
    setView("list");
    setSelectedId(null);
    setDetail(null);
    setForbidden(false);
    setDetailError("");
  };

  const byLevel = listSummary?.by_level || {};
  const emptyBecauseSearch = Boolean(search) || Boolean(level);

  const referral = detail?.referral || {};
  const staking = detail?.staking || { packages: [], commissions: [], totals: {} };
  const rewards = detail?.referral_rewards || { items: [], totals: {} };
  const brokerage = detail?.brokerage || { trades: [], totals: {} };

  const detailNeedsPager = useMemo(() => {
    const p = detailPagination;
    if (!p) return false;
    const maxTotal = Math.max(
      Number(p.staking_commission_total || 0),
      Number(p.referral_bonus_total || 0),
      Number(p.brokerage_trade_total || 0)
    );
    return maxTotal > (p.limit || PAGE_LIMIT);
  }, [detailPagination]);

  const detailTotalPages = useMemo(() => {
    const p = detailPagination;
    if (!p) return 1;
    const maxTotal = Math.max(
      Number(p.staking_commission_total || 0),
      Number(p.referral_bonus_total || 0),
      Number(p.brokerage_trade_total || 0)
    );
    return Math.max(1, Math.ceil(maxTotal / (p.limit || PAGE_LIMIT)));
  }, [detailPagination]);

  return (
    <div className={`rd-section ${view === "detail" ? "rd-section--detail" : ""}`}>
      {view !== "detail" && (
        <div className="exchange_earning_bnr rd-banner">
          <div className="earningbnr_cnt">
            <h2>My Referrals</h2>
            <p>See who you referred, what they staked, and what you earned.</p>
            {!token && (
              <button className="signbtn" onClick={() => navigate("/register")}>
                Sign Up Now
              </button>
            )}
          </div>
          <div className="earning_bnr">
            <img src="/images/earning_balance_vector.png" alt="My Referrals" />
          </div>
        </div>
      )}

      {!token && (
        <div className="dashboard_recent_s productdata">
          <div className="no-data-wrapper">
            <p>
              Please <Link to="/login">Login</Link> to continue
            </p>
          </div>
        </div>
      )}

      {token && view === "list" && (
        <>
          <div className="rd-stat-grid rd-stat-grid--levels">
            <div className="rd-stat-card rd-stat-card--earn">
              <div className="rd-stat-card__label">Total referrals</div>
              <div className="rd-stat-card__value">{listSummary.total_referrals || 0}</div>
              <div className="rd-stat-card__hint">Levels 1–5</div>
            </div>
            {[1, 2, 3, 4, 5].map((lv) => (
              <div className="rd-stat-card" key={lv}>
                <div className="rd-stat-card__label">Level {lv}</div>
                <div className="rd-stat-card__value">{byLevel[String(lv)] || 0}</div>
                <div className="rd-stat-card__hint">In your downline</div>
              </div>
            ))}
          </div>

          <div className="rd-toolbar">
            <div className="searchBar custom-tabs rd-search">
              <i className="ri-search-2-line" aria-hidden />
              <input
                type="search"
                className="custom_search"
                placeholder="Search name or UID"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div className="rd-chips">
              <button type="button" className={`rd-chip ${level === "" ? "active" : ""}`} onClick={() => setLevel("")}>
                All levels
              </button>
              {[1, 2, 3, 4, 5].map((lv) => (
                <button
                  type="button"
                  key={lv}
                  className={`rd-chip ${String(level) === String(lv) ? "active" : ""}`}
                  onClick={() => setLevel(String(lv))}
                >
                  L{lv} ({byLevel[String(lv)] || 0})
                </button>
              ))}
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => fetchList(pagination.page)}>
              Refresh
            </button>
          </div>

          {listError && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>{listError}</span>
              <button type="button" className="btn btn-sm custom-btn" onClick={() => fetchList(pagination.page)}>
                Retry
              </button>
            </div>
          )}

          <div className="dashboard_recent_s productdata position_order">
            <h4>Referral list</h4>
            <div className="table-responsive recenttable_s">
              <div className="desktop_view">
                <div className="table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>UID</th>
                        <th>Name</th>
                        <th>Level</th>
                        <th>Joined</th>
                        <th>Staked</th>
                        <th>Their payout</th>
                        <th>Your staking commission</th>
                        <th>Your referral bonus</th>
                        <th>Your brokerage</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listLoading ? (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            Loading referrals...
                          </td>
                        </tr>
                      ) : list.length > 0 ? (
                        list.map((row) => (
                          <tr
                            key={row.user_id}
                            className="rd-row-click"
                            onClick={() => openDetail(row.user_id)}
                          >
                            <td>{row.uuid || "—"}</td>
                            <td>{row.name || "—"}</td>
                            <td>
                              <span className="rd-badge">L{row.level}</span>
                            </td>
                            <td>{row.registered_at ? moment(row.registered_at).format("DD MMM YYYY") : "—"}</td>
                            <td>{fmtCoinList(row.summary?.staked_by_currency, "staked")}</td>
                            <td>{fmtCoinList(row.summary?.payout_received_by_currency, "payout_received")}</td>
                            <td className="text-green">
                              {fmtAmt(row.summary?.staking_commission_you_received)} ZILN
                            </td>
                            <td className="text-green">
                              {fmtAmt(row.summary?.referral_bonus_you_received)} ZILN
                            </td>
                            <td className="text-green">
                              {fmtAmt(row.summary?.brokerage_commission_you_received)} ZILN
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm custom-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDetail(row.user_id);
                                }}
                              >
                                View details
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="no-data-row">
                          <td colSpan="10">
                            <div className="no-data-wrapper">
                              <p>
                                {emptyBecauseSearch
                                  ? "No referrals match your search."
                                  : "You don’t have referrals in levels 1–5 yet."}
                              </p>
                              {emptyBecauseSearch && (
                                <button
                                  type="button"
                                  className="btn btn-sm custom-btn"
                                  onClick={() => {
                                    setSearchInput("");
                                    setSearch("");
                                    setLevel("");
                                  }}
                                >
                                  Clear search
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="order_history_mobile_view">
                {listLoading ? (
                  <p className="text-center py-4">Loading referrals...</p>
                ) : list.length > 0 ? (
                  list.map((row) => (
                    <div className="d-flex mb-3" key={row.user_id}>
                      <div className="order_datalist w-100">
                        <ul className="listdata">
                          <li>
                            <span className="date">{row.name || "—"}</span>
                            <span className="date_light">
                              L{row.level} · {row.uuid || "—"}
                            </span>
                          </li>
                          <li>
                            <span>Staking commission</span>
                            <span className="text-green">{fmtAmt(row.summary?.staking_commission_you_received)} ZILN</span>
                          </li>
                          <li>
                            <span>Referral bonus</span>
                            <span className="text-green">{fmtAmt(row.summary?.referral_bonus_you_received)} ZILN</span>
                          </li>
                          <li>
                            <span>Brokerage</span>
                            <span className="text-green">{fmtAmt(row.summary?.brokerage_commission_you_received)} ZILN</span>
                          </li>
                          <li>
                            <button type="button" className="btn btn-sm custom-btn w-100" onClick={() => openDetail(row.user_id)}>
                              View details
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-data-wrapper">
                    <p>
                      {emptyBecauseSearch
                        ? "No referrals match your search."
                        : "You don’t have referrals in levels 1–5 yet."}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {pagination.total_pages > 1 && (
              <div className="rd-pager">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchList(pagination.page - 1)}
                >
                  Prev
                </button>
                <span>
                  Page {pagination.page} of {pagination.total_pages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  disabled={pagination.page >= pagination.total_pages}
                  onClick={() => fetchList(pagination.page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {token && view === "detail" && (
        <>
          <div className="rd-toolbar">
            <button type="button" className="btn btn-sm btn-outline-primary rd-back" onClick={backToList}>
              ← Back to list
            </button>
          </div>

          {forbidden && (
            <div className="alert alert-warning">
              <p className="mb-2">You can only view your own referrals.</p>
              <button type="button" className="btn btn-sm custom-btn" onClick={backToList}>
                Back to list
              </button>
            </div>
          )}

          {detailError && !forbidden && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>{detailError}</span>
              <button type="button" className="btn btn-sm custom-btn" onClick={() => fetchDetail(selectedId, detailPage)}>
                Retry
              </button>
            </div>
          )}

          {detailLoading && <p className="text-center py-4">Loading referral details...</p>}

          {!detailLoading && detail && !forbidden && (
            <>
              <div className="rd-profile">
                <div className="rd-avatar">{initialsFromName(referral.name)}</div>
                <div>
                  <h3 className="rd-profile__name">{referral.name || "Referral details"}</h3>
                  <div className="rd-profile__meta">
                    <span>UID {referral.uuid || "—"}</span>
                    <span className="rd-badge">L{referral.level}</span>
                    {referral.registered_at && (
                      <span>Joined {moment(referral.registered_at).format("DD MMM YYYY")}</span>
                    )}
                    {referral.referral_code && <span>Code {referral.referral_code}</span>}
                  </div>
                </div>
              </div>

              <div className="rd-stat-grid">
                <div className="rd-stat-card rd-stat-card--earn">
                  <div className="rd-stat-card__label">Staking commission</div>
                  <div className="rd-stat-card__value">{fmtAmt(staking.totals?.commission_you_received)} ZILN</div>
                  <div className="rd-stat-card__hint">{staking.totals?.count || 0} records</div>
                </div>
                <div className="rd-stat-card rd-stat-card--earn">
                  <div className="rd-stat-card__label">Referral bonus</div>
                  <div className="rd-stat-card__value">{fmtAmt(rewards.totals?.commission_you_received)} ZILN</div>
                  <div className="rd-stat-card__hint">{rewards.totals?.count || 0} records</div>
                </div>
                <div className="rd-stat-card rd-stat-card--earn">
                  <div className="rd-stat-card__label">Brokerage commission</div>
                  <div className="rd-stat-card__value">{fmtAmt(brokerage.totals?.commission_you_received)} ZILN</div>
                  <div className="rd-stat-card__hint">{brokerage.totals?.count || 0} trades</div>
                </div>
              </div>

              <div className="rd-panel">
                <div className="rd-inner-tabs">
                  <button
                    type="button"
                    className={`rd-inner-tab ${innerTab === "staking" ? "active" : ""}`}
                    onClick={() => setInnerTab("staking")}
                  >
                    Staking
                    <span className="rd-tab-count">{staking.packages?.length || 0}</span>
                  </button>
                  <button
                    type="button"
                    className={`rd-inner-tab ${innerTab === "bonus" ? "active" : ""}`}
                    onClick={() => setInnerTab("bonus")}
                  >
                    Referral bonus
                    <span className="rd-tab-count">{detailPagination?.referral_bonus_total ?? rewards.items?.length ?? 0}</span>
                  </button>
                  <button
                    type="button"
                    className={`rd-inner-tab ${innerTab === "brokerage" ? "active" : ""}`}
                    onClick={() => setInnerTab("brokerage")}
                  >
                    Brokerage
                    <span className="rd-tab-count">{detailPagination?.brokerage_trade_total ?? brokerage.trades?.length ?? 0}</span>
                  </button>
                </div>

                <div className="rd-panel-body">
              {innerTab === "staking" && (
                <>
                  {(!staking.packages || staking.packages.length === 0) && (
                    <EmptyState text="This referral has not staked any package." />
                  )}
                  {(staking.packages || []).map((pkg) => {
                    const coin = pkg.package_currency || "—";
                    const pkgKey = pkg.staking_id || pkg.package_id;
                    const open = expandedPackage === pkgKey;
                    return (
                      <div className="rd-package-card" key={pkg.staking_id || pkg.package_id}>
                        <div className="rd-package-head">
                          <strong>
                            {pkg.duration_days || 0} days · {coin}
                          </strong>
                          <span className={statusClass(pkg.status)}>{pkg.status || "—"}</span>
                        </div>
                        {pkg.package_currency_fullname && (
                          <small className="text-muted d-block mb-2">{pkg.package_currency_fullname}</small>
                        )}
                        <div className="rd-kv">
                          <div>
                            <span>Staked</span>
                            {fmtAmt(pkg.invested_amount)} {coin}
                          </div>
                          <div>
                            <span>Payout received</span>
                            {fmtAmt(pkg.payout_received)} {coin}
                          </div>
                          <div>
                            <span>Remaining</span>
                            {fmtAmt(pkg.remaining_payout)} {coin}
                          </div>
                          <div>
                            <span>You earned</span>
                            <strong className="text-green">{fmtAmt(pkg.commission_you_received)} ZILN</strong>
                          </div>
                        </div>
                        {pkg.commission_share?.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-link px-0 mt-2"
                            onClick={() => setExpandedPackage(open ? null : pkgKey)}
                          >
                            {open ? "Hide commission share" : "Commission share"}
                          </button>
                        )}
                        {open && (
                          <div className="table-responsive mt-2">
                            <table>
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>%</th>
                                  <th>You received</th>
                                  <th>Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pkg.commission_share.map((share) => (
                                  <tr key={share._id}>
                                    <td>{commissionTypeLabel(share.commission_type)}</td>
                                    <td>{fmtAmt(share.commission_percent)}%</td>
                                    <td className="text-green">
                                      {fmtAmt(share.commission_amount)} {share.credited_currency || "ZILN"}
                                    </td>
                                    <td>
                                      {share.credited_at ? moment(share.credited_at).format("DD/MM/YYYY hh:mm A") : "—"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {(staking.commissions || []).length > 0 && (
                    <div className="rd-table-wrap mt-3">
                      <h4>Staking commission history</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Level</th>
                            <th>Payout base</th>
                            <th>%</th>
                            <th>You received</th>
                          </tr>
                        </thead>
                        <tbody>
                          {staking.commissions.map((row) => (
                            <tr key={row._id}>
                              <td>{row.credited_at ? moment(row.credited_at).format("DD/MM/YYYY hh:mm A") : "—"}</td>
                              <td>{commissionTypeLabel(row.commission_type)}</td>
                              <td>L{row.level}</td>
                              <td>{fmtAmt(row.payout_amount)}</td>
                              <td>{fmtAmt(row.commission_percent)}%</td>
                              <td className="text-green">
                                {fmtAmt(row.commission_amount)} {row.credited_currency || "ZILN"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {innerTab === "bonus" && (
                (rewards.items || []).length > 0 ? (
                  <div className="rd-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Level</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rewards.items.map((item) => (
                          <tr key={item._id}>
                            <td>{item.credited_at ? moment(item.credited_at).format("DD/MM/YYYY hh:mm A") : "—"}</td>
                            <td>{rewardTypeLabel(item.type)}</td>
                            <td>L{item.level}</td>
                            <td className="text-green">
                              {fmtAmt(item.amount)} {item.credited_currency || "ZILN"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState text="No referral bonus from this user yet." />
                )
              )}

              {innerTab === "brokerage" && (
                (brokerage.trades || []).length > 0 ? (
                  <div className="rd-table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Order</th>
                          <th>Pair</th>
                          <th>Side</th>
                          <th>Qty</th>
                          <th>Price</th>
                          <th>Fee</th>
                          <th>Your commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brokerage.trades.map((trade) => (
                          <tr key={trade._id}>
                            <td>{trade.traded_at ? moment(trade.traded_at).format("DD/MM/YYYY hh:mm A") : "—"}</td>
                            <td>{trade.order_id || String(trade.trade_id || "—").slice(-8)}</td>
                            <td>{trade.pair || "—"}</td>
                            <td>
                              <span className={`rd-badge ${String(trade.side).toLowerCase()}`}>{trade.side || "—"}</span>
                            </td>
                            <td>{fmtAmt(trade.quantity)}</td>
                            <td>{fmtAmt(trade.price)}</td>
                            <td>
                              {fmtAmt(trade.trade_fee)} {trade.fee_currency || ""}
                            </td>
                            <td className="text-green" title={trade.source_commission_amount ? `Source: ${trade.source_commission_amount} ${trade.fee_currency || ""}` : undefined}>
                              {fmtAmt(trade.commission_amount)} {trade.credited_currency || "ZILN"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState text="No trades from this referral have paid you brokerage yet." />
                )
              )}
                </div>
              </div>

              {detailNeedsPager && (
                <div className="rd-pager">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={detailPage <= 1}
                    onClick={() => fetchDetail(selectedId, detailPage - 1)}
                  >
                    Prev
                  </button>
                  <span>
                    Page {detailPage} of {detailTotalPages}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={detailPage >= detailTotalPages}
                    onClick={() => fetchDetail(selectedId, detailPage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReferralDetailsSection;
